'use client';

import { useMemo } from 'react';

import {
  ALL_CURRENCIES,
  COMMON_CURRENCY_CODES,
  DEFAULT_CURRENCY,
  getCurrencyName,
} from '@kit/shared/currency';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';

/**
 * Currency picker over the full ISO 4217 set.
 *
 * Each of these lists used to be hardcoded per form, and they disagreed —
 * suppliers offered SEK and ZAR but not AUD, so an Australian user could not
 * record a supplier in their own currency at all.
 *
 * The common trade currencies stay pinned at the top since they cover nearly
 * every order, but no currency is unreachable.
 */
export function CurrencySelect(props: {
  value: string | null | undefined;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * Show only the code on the trigger, with the full name still in the menu.
   *
   * The order page's picker sits inline in a header row and read
   * "USD (US Dolla" — a truncated word, which looks like a rendering fault
   * rather than a control. No width fixes that in general: the trigger has to
   * hold "AED (United Arab Emirates Dirham)" as easily as "USD (US Dollar)".
   * The code alone is what identifies a currency anyway, and the names are
   * there when choosing one.
   */
  compact?: boolean;
  'data-test'?: string;
}) {
  const rest = useMemo(
    () =>
      ALL_CURRENCIES.filter(
        (currency) =>
          !COMMON_CURRENCY_CODES.includes(
            currency.code as (typeof COMMON_CURRENCY_CODES)[number],
          ),
      ),
    [],
  );

  return (
    <Select
      disabled={props.disabled}
      onValueChange={props.onChange}
      value={props.value ?? DEFAULT_CURRENCY}
    >
      <SelectTrigger
        className={props.className}
        data-test={props['data-test']}
        id={props.id}
      >
        <SelectValue placeholder={props.placeholder ?? 'Select currency'}>
          {props.compact ? (props.value ?? DEFAULT_CURRENCY) : undefined}
        </SelectValue>
      </SelectTrigger>

      <SelectContent>
        <SelectGroup>
          <SelectLabel>Common</SelectLabel>

          {COMMON_CURRENCY_CODES.map((code) => (
            <SelectItem key={code} value={code}>
              {code} ({getCurrencyName(code)})
            </SelectItem>
          ))}
        </SelectGroup>

        <SelectSeparator />

        <SelectGroup>
          <SelectLabel>All currencies</SelectLabel>

          {rest.map((currency) => (
            <SelectItem key={currency.code} value={currency.code}>
              {currency.code} ({getCurrencyName(currency.code)})
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
